<template>
    <div class="flex flex-col items-start py-3 px-4">
        <div class="my-1 flex justify-between w-full">
            <span class="font-semibold text-md">
                {{ $t("common.posixToDate.ingressPosixDate") }}
            </span>
            <div class="flex items-center gap-2">
                <div v-if="!isEditingAlias" class="flex items-center gap-1">
                    <span class="font-semibold text-md">
                        {{ alias }}
                    </span>
                    <button
                        @click="startEditingAlias"
                        type="button"
                        class="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                        :aria-label="$t('common.editAlias')"
                    >
                        <pencil-icon
                            class="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        />
                    </button>
                </div>
                <div v-else class="flex items-center gap-1">
                    <input
                        ref="aliasInput"
                        v-model="tempAlias"
                        type="text"
                        class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-emphasis"
                        @keyup.enter="saveAlias"
                        @keyup.escape="cancelEditingAlias"
                        @blur="saveAlias"
                    />
                    <button
                        @click="saveAlias"
                        type="button"
                        class="p-1 hover:bg-green-200 dark:hover:bg-green-700 rounded transition-colors"
                        :aria-label="$t('common.saveAlias')"
                    >
                        <check
                            class="h-4 w-4 text-green-600 dark:text-green-400"
                        />
                    </button>
                    <button
                        @click="cancelEditingAlias"
                        type="button"
                        class="p-1 hover:bg-red-200 dark:hover:bg-red-700 rounded transition-colors"
                        :aria-label="$t('common.cancelEdit')"
                    >
                        <cancel
                            class="h-4 w-4 text-red-600 dark:text-red-400"
                        />
                    </button>
                </div>
            </div>
        </div>
        <div class="flex mt-2">
            <input-component
                :id="id"
                v-model:value="input"
                type="number"
                field-name=""
                :placeholder="$t('common.posixToDate.placeholder')"
                @enter="parseDate"
            />
            <button
                @click="parseDate"
                type="button"
                class="px-4"
                :aria-label="$t('common.posixToDate.convertPosix')"
            >
                <arrow-simple-right
                    class="h-5 w-5 text-primary-emphasis dark:text-primary-details hover:text-primary-primary"
                />
            </button>
        </div>
        <div class="mt-3 w-full">
            <div v-if="error">
                <span class="text-red-700 text-lg">{{ error }}</span>
            </div>
            <div v-else>
                <div>
                    <small v-if="type">
                        {{ $t("common.posixToDate.assumingTimestamp") }}
                        <span class="font-semibold">
                            {{ type }}
                        </span>
                    </small>
                </div>
                <div class="flex items-center">
                    <span class="w-1/2">
                        {{ $t("common.posixToDate.utcIsoFormat") }}
                    </span>
                    <div
                        v-if="resultUTC"
                        class="flex items-center w-1/2 justify-end"
                    >
                        <small>
                            {{ resultUTC }}
                        </small>
                        <copy-clipboard-button
                            :input="resultUTC"
                            class="fill-blue-500 ml-2"
                        />
                    </div>
                    <span v-else>{{ $t("common.posixToDate.loading") }}</span>
                </div>
                <div class="flex items-center">
                    <span class="w-1/2">
                        {{ $t("common.posixToDate.localIsoFormat") }}
                        ({{ timeZone }})
                    </span>
                    <div
                        v-if="resultTimeZone"
                        class="flex items-center justify-end w-1/2"
                    >
                        <small>{{ resultTimeZone }}</small>
                        <copy-clipboard-button
                            :input="resultTimeZone"
                            class="fill-blue-500 ml-2"
                        />
                    </div>
                    <span v-else>{{ $t("common.posixToDate.loading") }}</span>
                </div>
                <div class="flex items-center">
                    <span class="w-1/2">
                        {{ $t("common.posixToDate.local") }}
                    </span>
                    <div
                        v-if="resultTimeZoneH"
                        class="flex items-center justify-end w-1/2"
                    >
                        <small>{{ resultTimeZoneH }}</small>
                        <copy-clipboard-button
                            :input="resultTimeZoneH"
                            class="fill-blue-500 ml-2"
                        />
                    </div>
                    <span v-else>{{ $t("common.posixToDate.loading") }}</span>
                </div>
                <div class="flex">
                    <span class="w-1/2">
                        {{ $t("common.posixToDate.unixSeconds") }}
                    </span>
                    <div
                        v-if="resultSeconds"
                        class="w-1/2 flex items-center justify-end"
                    >
                        <small class="">
                            {{ resultSeconds }}
                        </small>
                        <copy-clipboard-button
                            :input="resultSeconds"
                            class="fill-blue-500 ml-2"
                        />
                    </div>
                    <span v-else>{{ $t("common.posixToDate.loading") }}</span>
                </div>
                <div class="flex">
                    <span class="w-1/2">
                        {{ $t("common.posixToDate.unixMilliseconds") }}
                    </span>
                    <div
                        v-if="resultMilliseconds"
                        class="w-1/2 flex items-center justify-end"
                    >
                        <small class="">
                            {{ resultMilliseconds }}
                        </small>
                        <copy-clipboard-button
                            :input="`${resultMilliseconds}`"
                            class="fill-blue-500 ml-2"
                        />
                    </div>
                    <span v-else>{{ $t("common.posixToDate.loading") }}</span>
                </div>
                <div class="flex">
                    <span class="w-1/2">
                        {{ $t("common.posixToDate.unixMicroseconds") }}
                    </span>
                    <div
                        v-if="resultMicroseconds"
                        class="w-1/2 flex items-center justify-end"
                    >
                        <small class="">
                            {{ resultMicroseconds }}
                        </small>
                        <copy-clipboard-button
                            :input="`${resultMicroseconds}`"
                            class="fill-blue-500 ml-2"
                        />
                    </div>
                    <span v-else>{{ $t("common.posixToDate.loading") }}</span>
                </div>
                <div class="flex">
                    <span class="w-1/2">
                        {{ $t("common.posixToDate.unixNanoseconds") }}
                    </span>
                    <div
                        v-if="resultNanoseconds"
                        class="w-1/2 flex items-center justify-end"
                    >
                        <small class="">
                            {{ resultNanoseconds }}
                        </small>
                        <copy-clipboard-button
                            :input="`${resultNanoseconds}`"
                            class="fill-blue-500 ml-2"
                        />
                    </div>
                    <span v-else>{{ $t("common.posixToDate.loading") }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import type { DatesAdapter } from "inversify-utils-kit";
import { defineComponent, inject, nextTick, reactive, ref, toRefs } from "vue";

import { toCapitalizedSimple } from "@/common/lib/formatters";

import ArrowSimpleRight from "@/components/icons/ArrowSimpleRight.vue";
import Cancel from "@/components/icons/Cancel.vue";
import Check from "@/components/icons/Check.vue";
import PencilIcon from "@/components/icons/Pencil.vue";
import CopyClipboardButton from "@/components/utils/CopyClipboardButton.vue";
import InputComponent from "@/components/utils/InputComponent.vue";

export default defineComponent({
    components: {
        InputComponent,
        CopyClipboardButton,
        ArrowSimpleRight,
        PencilIcon,
        Check,
        Cancel
    },
    name: "PosixToDateConverter",
    props: {
        id: {
            default: () => "1",
            required: false,
            type: String
        }
    },
    setup({ id }) {
        const datesAdapter = inject("datesAdapter") as DatesAdapter;
        const aliasInput = ref<HTMLInputElement | null>(null);

        const timeZone = datesAdapter.TIMEZONE;
        const state = reactive({
            resultTimeZone: "",
            resultTimeZoneH: "",
            resultSeconds: "",
            resultMilliseconds: "",
            resultMicroseconds: "",
            resultNanoseconds: "",
            resultUTC: "",
            input: "",
            posix: "",
            timeZone,
            error: "",
            type: "",
            alias: "",
            isEditingAlias: false,
            tempAlias: ""
        });

        state.input = `${datesAdapter.toUnix(datesAdapter.now())}`;
        state.alias = `#${id}`;

        const parseDate = async () => {
            state.error = "";
            state.type = "...";
            state.posix = "...";
            state.resultSeconds = "...";
            state.resultMilliseconds = "...";
            state.resultMicroseconds = "...";
            state.resultNanoseconds = "...";
            state.resultUTC = "...";
            state.resultTimeZone = "...";
            state.resultTimeZoneH = "...";
            await new Promise((resolve) => setTimeout(resolve, 300));

            try {
                const { posix, type } = datesAdapter.fromUnix(state.input);
                state.type = type;
                state.posix = `${datesAdapter.toUnix(posix)}`;
                state.resultSeconds = `${state.posix}`;
                state.resultMilliseconds = `${state.posix}000`;
                state.resultMicroseconds = `${state.resultMilliseconds}000`;
                state.resultNanoseconds = `${state.resultMicroseconds}000`;
                state.resultUTC = datesAdapter.toUTC(posix);
                state.resultTimeZone = datesAdapter.toLocal(posix);
                state.resultTimeZoneH = toCapitalizedSimple(
                    datesAdapter.toFormat(posix, "DDDD h:mm:ss a")
                );
            } catch (error: any) {
                state.error = `Error: ${error.message}`;
                console.error(error);
                return;
            }
        };

        const startEditingAlias = async () => {
            state.tempAlias = state.alias;
            state.isEditingAlias = true;
            await nextTick();
            aliasInput.value?.focus();
            aliasInput.value?.select();
        };

        const saveAlias = () => {
            if (state.tempAlias.trim()) {
                state.alias = state.tempAlias.trim();
            }
            state.isEditingAlias = false;
        };

        const cancelEditingAlias = () => {
            state.tempAlias = "";
            state.isEditingAlias = false;
        };

        return {
            ...toRefs(state),
            aliasInput,
            parseDate,
            startEditingAlias,
            saveAlias,
            cancelEditingAlias
        };
    }
});
</script>
